import { CheckCircle2, IndianRupee, Clock, TrendingUp, Calendar, AlertCircle, ArrowLeft, Download, Building2, User, FileText } from "lucide-react";
import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
import { KpiCard } from "../../../Component/AdminComponents/KpiCard";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import { Badge } from "../../../Component/AdminComponents/Badge";
import { useState, useMemo } from "react";
import BlueButton from "../../../Component/Common/BlueButton";
import SearchInput from "../../../Component/Common/SearchInput";
import { useNavigate } from "react-router-dom";

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const commissionPayouts = [
  {
    id: "PAY-9001",
    forType: "MCP",
    entityName: "Rahul Patil",
    period: "Week 42 · 2025",
    role: "Onboarding Commission",
    commissionAmount: 22000,
    payableAmount: 20000,
    transferred: true,
    bankName: "HDFC Bank",
    accountLast4: "1234",
    txnId: "TXN-HDFC-2025-001",
    date: "2025-10-25",
  },
  {
    id: "PAY-9002",
    forType: "MCP",
    entityName: "Shree Mobile Store",
    period: "Week 42 · 2025",
    role: "Warranty Sales Payout",
    commissionAmount: 12000,
    payableAmount: 12000,
    transferred: false,
    bankName: "ICICI Bank",
    accountLast4: "5678",
    txnId: "-",
    date: "2025-10-27",
  },
  {
    id: "PAY-9003",
    forType: "CP",
    entityName: "Priya Singh",
    period: "Week 41 · 2025",
    role: "Warranty Sales Payout",
    commissionAmount: 15000,
    payableAmount: 15000,
    transferred: true,
    bankName: "SBI",
    accountLast4: "9999",
    txnId: "TXN-SBI-2025-221",
    date: "2025-10-20",
  },
  {
    id: "PAY-9004",
    forType: "Promoter",
    entityName: "A1 Electronics",
    period: "Week 43 · 2025",
    role: "Warranty Sales Payout",
    commissionAmount: 8500,
    payableAmount: 8500,
    transferred: false,
    bankName: "Axis Bank",
    accountLast4: "3421",
    txnId: "-",
    date: "2025-11-01",
  },
  {
    id: "PAY-9005",
    forType: "CP",
    entityName: "Kiran Desai",
    period: "Week 43 · 2025",
    role: "Warranty Sales Payout",
    commissionAmount: 18000,
    payableAmount: 17000,
    transferred: false,
    bankName: "Kotak Bank",
    accountLast4: "7812",
    txnId: "-",
    date: "2025-11-02",
  },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const todayISO = () => new Date().toISOString().slice(0, 10);
const currentMonth = new Date().toISOString().slice(0, 7); // "2025-11"

// ─────────────────────────────────────────────────────────────────────────────
//  ATOM: TabButton
// ─────────────────────────────────────────────────────────────────────────────
function TabButton({ label, active, count, onClick, accent }) {
  return (
    <button
      onClick={onClick}
      className="relative flex items-center gap-2 px-5 py-3 text-[13px] font-bold
        transition-all duration-150 border-b-2 focus:outline-none"
      style={{
        borderBottomColor: active ? accent : "transparent",
        color:             active ? accent : "#94A3B8",
        background:        "transparent",
      }}
    >
      {label}
      {count !== undefined && (
        <span
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black"
          style={{
            background: active ? accent : "#F1F5F9",
            color:      active ? "#fff"  : "#64748B",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOLECULE: StatCard  — matching reference image style
// ─────────────────────────────────────────────────────────────────────────────
function StatCard({ label, value, sublabel, icon: Icon, accent, bg, index }) {
  return (
    <div
      className="rounded-2xl p-5 flex justify-between gap-3 animate-fade-in-up"
      style={{
        background: "#FFFFFF",
        border:     `1px solid ${bg}`,
        boxShadow:  "0 2px 12px rgba(0,0,0,0.05)",
        animationDelay: `${index * 0.06}s`,
      }}
    >
      

      {/* Value */}
      <div>
        <p
          className="text-[20px] font-black leading-none mb-1"
          style={{ color: accent, fontFamily:"'Plus Jakarta Sans',sans-serif" }}
        >
          {value}
        </p>
        <p className="text-[12px] font-bold text-slate-600">{label}</p>
        {sublabel && (
          <p className="text-[11px] text-slate-400 mt-0.5">{sublabel}</p>
        )}
      </div>

      {/* Icon */}
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: bg }}
      >
        <Icon size={18} style={{ color: accent }}/>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOLECULE: CommissionTable  — shared by both tabs
// ─────────────────────────────────────────────────────────────────────────────
function CommissionTable({ list, onToggle,activeTab, Navigate, setSelectedSummary }) {
  if (!list.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <AlertCircle size={28} className="mb-2 opacity-40"/>
        <p className="text-sm font-semibold">No records found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr
            className="text-left"
            style={{ borderBottom:"1px solid #F1F5F9" }}
          >
           {[
              "Unbilled ID",
              "Entity",
              "Role / Period",
              "Amounts (₹)",
              ...(activeTab !== "current" ? ["Bank / Txn", "Date"] : []),
              "Status",
              "Action",
            ].map(h => (
              <th
                key={h}
                className="pb-3 pr-4 font-black uppercase tracking-widest"
                style={{ fontSize:10, color:"#94A3B8" }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.map((p, i) => (
            <tr
              key={p.id}
              className="group hover:bg-slate-50/60 transition-colors"
              style={{ borderBottom:"1px solid #F8FAFC" }}
            >
              {/* ID */}
              <td className="py-3 pr-4">
                <span className="font-bold text-slate-700">{p.id}</span>
              </td>

              {/* Entity */}
              <td className="py-3 pr-4">
                <p className="font-bold text-slate-800 whitespace-nowrap">{p.entityName}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{p.forType}</p>
              </td>

              {/* Role / Period */}
              <td className="py-3 pr-4">
                <p className="text-slate-700 font-semibold whitespace-nowrap">{p.role}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{p.date} to {p.date}</p>
              </td>

              {/* Amounts */}
              <td className="py-3 pr-4">
                <p className="font-semibold text-slate-700">₹{formatCurrency(p.commissionAmount)}</p>
                {/* <p className="text-[10px] text-slate-400 mt-0.5">
                  Payable: ₹{formatCurrency(p.payableAmount)}
                </p> */}
              </td>

              {/* Bank / Txn */}
              { activeTab !== "current" && (
                <td className="py-3 pr-4">
                <p className="font-semibold text-slate-700 whitespace-nowrap">
                  {p.bankName} · ****{p.accountLast4}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">Txn: {p.txnId}</p>
              </td>

              )}
              { activeTab !== "current" && (
              <td className="py-3 pr-4">
                <p className="text-slate-600 whitespace-nowrap">{p.date}</p>
              </td>
              )}

              {/* Status */}
              <td className="py-3 pr-4">
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold"
                  style={{
                    background: p.transferred ? "#ECFDF5" : "#FFFBEB",
                    color:      p.transferred ? "#059669" : "#D97706",
                    border:     `1px solid ${p.transferred ? "#A7F3D0" : "#FDE68A"}`,
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: p.transferred ? "#10B981" : "#F59E0B" }}
                  />
                  {p.transferred ? "Transferred" : "Pending"}
                </span>
              </td>

              {/* Action */}
              <td className="py-3 ">
                <div className="flex gap-2">
                  { activeTab !== "current" && (
                  <button
                  onClick={() => onToggle(p.id)}
                  className="px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                  style={{
                    background:  p.transferred ? "#FEF2F2" : "#EFF6FF",
                    color:       p.transferred ? "#EF4444" : "#2563EB",
                    border:      `1px solid ${p.transferred ? "#FECACA" : "#BFDBFE"}`,
                  }}
                >
                  {p.transferred ? "Mark Pending" : "Mark Transferred"}
                </button>
                  )}

                  {activeTab === "unbilled" && (
                    <button
                      onClick={() => setSelectedSummary(p)}
                      className="px-3 py-1.5 rounded-xl text-[11px] font-bold bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100"
                    >
                      Summary
                    </button>
                  )}
                <BlueButton onClick={() =>
                        Navigate(`/commission-details`)
                      }>
                  View
                </BlueButton>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ─── SUMMARY COMPONENT ────────────────────────────────────────────────────────
// function PayoutSummary({ data, onBack }) {
//   const PRODUCTS = [
//     { name: "Extended Warranty", rate: 350, units: 12 },
//     { name: "Screen Damage", rate: 220, units: 8 },
//     { name: "Total Protection", rate: 480, units: 5 },
//   ];

//   const subtotal = PRODUCTS.reduce((acc, p) => acc + (p.rate * p.units), 0);
//   const gstAmount = subtotal * 0.18; // 18% GST
//   const grandTotal = subtotal + gstAmount;

//   return (
//     <div className="space-y-6 animate-fade-in">
//       {/* Header with Back Button */}
//       <div className="flex items-center justify-between">
//         <button 
//           onClick={onBack}
//           className="flex items-center gap-2 text-slate-500 hover:text-blue-600 font-bold text-sm transition-colors"
//         >
//           <ArrowLeft size={18} /> Back to Register
//         </button>
//         <BlueButton onClick={() => window.print()}>
//           <Download size={14} className="mr-2" /> Download PDF
//         </BlueButton>
//       </div>

//       <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
//         {/* Top Branding Section */}
//         <div className="bg-slate-900 p-8 text-white flex justify-between items-start">
//           <div>
//             <h2 className="text-2xl font-black tracking-tight italic">GOELECTRONIX</h2>
//             <p className="text-slate-400 text-[11px] mt-1 uppercase tracking-widest font-bold">Official Commission Statement</p>
//           </div>
//           <div className="text-right">
//             <p className="text-[11px] font-bold text-slate-400 uppercase">Statement ID</p>
//             <p className="font-mono text-sm">{data.id.replace('PAY', 'STMT')}</p>
//           </div>
//         </div>

//         <div className="p-8 space-y-8">
//           {/* Address Grid */}
//           <div className="grid grid-cols-2 gap-12">
//             <div className="space-y-3">
//               <div className="flex items-center gap-2 text-blue-600">
//                 <Building2 size={16} />
//                 <span className="text-[10px] font-black uppercase tracking-widest">Company Details</span>
//               </div>
//               <div className="text-slate-700">
//                 <p className="font-bold text-sm">GoElectronix Solutions Pvt Ltd</p>
//                 <p className="text-xs text-slate-500 leading-relaxed mt-1">
//                   Plot No. 42, Tech Park, Sector 11,<br />
//                   Navi Mumbai, Maharashtra - 400706<br />
                  
//                 </p>
//               </div>
//             </div>

//             <div className="space-y-3">
//               <div className="flex items-center gap-2 text-amber-600">
//                 <User size={16} />
//                 <span className="text-[10px] font-black uppercase tracking-widest">Entity Details</span>
//               </div>
//               <div className="text-slate-700">
//                 <p className="font-bold text-sm">{data.entityName}</p>
//                 <p className="text-xs text-slate-500 leading-relaxed mt-1">
//                   Type: {data.forType}<br />
//                   Bank: {data.bankName} (****{data.accountLast4})<br />
//                   Period: {data.date} to {data.date} <br />
//                   GSTIN: 27AAACG0000A1Z5
//                 </p>
//               </div>
//             </div>
//           </div>

//           {/* Product Breakdown Table */}
//           <div className="space-y-4">
//             <div className="flex items-center gap-2 text-slate-800">
//               <FileText size={16} />
//               <span className="text-[10px] font-black uppercase tracking-widest">Payout Summary</span>
//             </div>
//             <table className="w-full text-sm">
//               <thead>
//                 <tr className="text-left border-b border-slate-100">
//                   <th className="py-3 text-slate-400 font-bold text-[10px] uppercase">Product Description</th>
//                   <th className="py-3 text-slate-400 font-bold text-[10px] uppercase text-center">Units</th>
//                   <th className="py-3 text-slate-400 font-bold text-[10px] uppercase text-right">Rate</th>
//                   <th className="py-3 text-slate-800 font-bold text-[10px] uppercase text-right">Total Payout</th>
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-slate-50">
//                 {PRODUCTS.map((prod) => (
//                   <tr key={prod.name}>
//                     <td className="py-4 font-bold text-slate-700">{prod.name}</td>
//                     <td className="py-4 text-center text-slate-600 font-medium">{prod.units}</td>
//                     <td className="py-4 text-right text-slate-600">₹{prod.rate}</td>
//                     <td className="py-4 text-right font-bold text-slate-900">₹{(prod.rate * prod.units).toLocaleString()}</td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>

//           {/* Calculation Section */}
//           <div className="flex justify-end">
//             <div className="w-full max-w-[300px] space-y-3 border-t-2 border-slate-900 pt-4">
//               <div className="flex justify-between text-xs font-bold text-slate-500">
//                 <span>Subtotal Payout</span>
//                 <span>₹{subtotal.toLocaleString()}</span>
//               </div>
//               <div className="flex justify-between text-xs font-bold text-slate-500">
//                 <span>GST (18%)</span>
//                 <span>₹{gstAmount.toLocaleString()}</span>
//               </div>
//               <div className="flex justify-between items-center text-lg font-black text-slate-900 pt-2 border-t border-slate-100">
//                 <span>Total Amount</span>
//                 <span className="text-blue-600">₹{grandTotal.toLocaleString()}</span>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

function PayoutSummary({ data, onBack }) {
  const PRODUCTS = [
    { name: "Extended Warranty", hsn: "999523", mrp: 500, units: 12, rate: 350 },
    { name: "Screen Damage Protection", hsn: "999524", mrp: 300, units: 8, rate: 220 },
    { name: "Total Protection Plan", hsn: "999525", mrp: 600, units: 5, rate: 480 },
  ];

  const GST_RATE = 0.09; // 9% CGST + 9% SGST
  const DISCOUNT = 120;

  const subtotal = PRODUCTS.reduce((acc, p) => acc + p.rate * p.units, 0);
  const cgst = subtotal * GST_RATE;
  const sgst = subtotal * GST_RATE;
  const total = subtotal - DISCOUNT;

  const formatNum = (n) => n.toLocaleString("en-IN", { minimumFractionDigits: 2 });

  return (
    <div className="space-y-4">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft size={16} /> Back to Register
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-lg"
        >
          <Download size={13} /> Download PDF
        </button>
      </div>

      {/* Invoice Card */}
      <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden text-xs">

        {/* Logo + Company */}
          <div className="space-y-1.5">
            <div className="bg-slate-900 p-8 text-white flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-black tracking-tight italic">GOELECTRONIX</h2>
              <p className="text-slate-400 text-[11px] mt-1 uppercase tracking-widest font-bold">Official Commission Statement</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-bold text-slate-400 uppercase">Statement ID</p>
              <p className="font-mono text-sm">{data.id.replace('PAY', 'STMT')}</p>
            </div>
          </div>

        {/* ── HEADER ── */}
        {/* <div
          className="grid gap-4 p-5 border-b-2 border-slate-900"
          style={{ gridTemplateColumns: "auto 1fr auto" }}
        >
          
            <div className="text-md text-slate-500 leading-relaxed">
              <p className="font-bold text-slate-800 text-xs">
                {data.entityName}
                <span className="ml-2 text-md font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                    MCP
                  </span>
              </p>
              
              <p> Mr. A. Kumar, 12/A, Gandhi Nagar, </p>
              <p>Mumbai - 400001</p>
              <p>Period: 2025-Apr-27 to 2025-Apr-27</p>
              <p>GSTIN: 27ABDC59362G1Z0</p>
            </div>
          </div> */}

          <div />

          {/* Invoice Meta */}
          {/* <div className="text-right">
            <p className="text-2xl font-black text-slate-900 tracking-tight">Performa Invoice</p>
            <p className="text-md font-bold uppercase tracking-widest text-slate-400 mb-3">
              Official Commission Statement
            </p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-left text-[10px]">
              {[
                ["Invoice #", data.id.replace("PAY", "INV")],
                ["Invoice Date", data.date],
                // ["Terms", "Credit 7 Days"],
                // ["Due Date", "—"],
                // ["Place of Supply", "Maharashtra (27)"],
              ].map(([lbl, val]) => (
                <>
                  <span className="text-slate-400 font-semibold">{lbl}</span>
                  <span className="font-bold text-slate-800 font-mono">{val}</span>
                </>
              ))}
            </div>
          </div> */}
        </div>

        {/* ── BILL TO / SHIP TO ── */}
        <div className="grid grid-cols-2 border-b border-slate-200">
          {[
            {
              logo: User,
              label: "Entity Details",
              name: `${data.entityName}`,
              badge: data.forType,
              body: `Type: ${data.forType}\nBank: ${data.bankName} (****${data.accountLast4})\nPeriod: ${data.date} to ${data.date}\nGSTIN: 27AAACG0000A1Z5`,
            },
            {
              logo: Building2,
              label: "Company Details",
              name: "GoElectronix Solutions Pvt Ltd",
              badge: null,
              body: "Plot No. 42, Tech Park, Sector 11,\nNavi Mumbai, Maharashtra - 400706",
            },
          ].map((block, i) => (
            <div
              key={i}
              className={`p-4 ${i === 1 ? "border-l border-slate-200" : ""}`}
            >
              <p className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-amber-600 mb-1.5">
                {block.logo && <block.logo className="w-3 h-3" />}
                {block.label}
              </p>
              <p className="font-bold text-slate-900 text-[11px] mb-1">
                {block.name}
                {block.badge && (
                  <span className="ml-2 text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                    {block.badge}
                  </span>
                )}
              </p>
              <p className="text- text-slate-500 leading-relaxed whitespace-pre-line">
                {block.body}
              </p>
            </div>
          ))}
        </div>

        {/* ── PRODUCT TABLE ── */}
        <table className="w-full text-[10px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="py-2 px-3 text-left text-[9px] font-black uppercase tracking-wider text-slate-500 w-6">#</th>
              <th className="py-2 px-3 text-left text-[9px] font-black uppercase tracking-wider text-slate-500">Item & Description</th>
              <th className="py-2 px-3 text-[9px] font-black uppercase tracking-wider text-slate-500">HSN/SAC</th>
              <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-500">MRP</th>
              <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-500">Qty</th>
              <th className="py-2 px-3 text-right text-[9px] font-black uppercase tracking-wider text-slate-500">Rate</th>
              <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-400 bg-blue-50/40" colSpan={2}>CGST</th>
              <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-400 bg-indigo-50/40" colSpan={2}>SGST</th>
              <th className="py-2 px-3 text-right text-[9px] font-black uppercase tracking-wider text-slate-700">Amount</th>
            </tr>
            <tr className="bg-slate-50 border-b border-slate-200">
              {["","","","","","","%","Amt","%","Amt",""].map((h, i) => (
                <th key={i} className="py-1 px-3 text-[9px] font-bold text-slate-400 text-center">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PRODUCTS.map((p, i) => {
              const lineTotal = p.rate * p.units;
              const lineCgst = lineTotal * GST_RATE;
              return (
                <tr key={p.name} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="py-3 px-3 text-slate-500">{i + 1}</td>
                  <td className="py-3 px-3">
                    <p className="font-bold text-slate-800">{p.name}</p>
                    <p className="text-[9px] text-slate-400">Warranty plan for Android smartphones</p>
                  </td>
                  <td className="py-3 px-3 font-mono text-slate-600">{p.hsn}</td>
                  <td className="py-3 px-3 text-center font-mono">{p.mrp}</td>
                  <td className="py-3 px-3 text-center font-mono">{p.units} pcs</td>
                  <td className="py-3 px-3 text-right font-mono">{p.rate}.00</td>
                  <td className="py-3 px-3 text-center font-mono bg-blue-50/20">9%</td>
                  <td className="py-3 px-3 text-center font-mono bg-blue-50/20">{formatNum(lineCgst)}</td>
                  <td className="py-3 px-3 text-center font-mono bg-indigo-50/20">9%</td>
                  <td className="py-3 px-3 text-center font-mono bg-indigo-50/20">{formatNum(lineCgst)}</td>
                  <td className="py-3 px-3 text-right font-mono font-bold text-slate-800">
                    {formatNum(lineTotal)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* ── TOTALS ROW ── */}
        <div className="grid border-t border-slate-200" style={{ gridTemplateColumns: "1fr 280px" }}>
          <div className="p-4 border-r border-slate-200">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Total In Words</p>
            <p className="font-bold text-slate-800 text-[11px]">Indian Rupee Ten Thousand Only</p>
          </div>
          <div className="text-[10px]">
            {[
              { label: "Sub Total (Tax Inclusive)", val: formatNum(subtotal), cls: "bg-slate-50" },
              { label: "CGST (9%)", val: formatNum(cgst), cls: "" },
              { label: "SGST (9%)", val: formatNum(sgst), cls: "" },
              // { label: "Discount", val: `(-) ${formatNum(DISCOUNT)}`, cls: "text-red-500" },
            ].map(({ label, val, cls }) => (
              <div
                key={label}
                className={`flex justify-between px-5 py-2 border-b border-slate-100 ${cls}`}
              >
                <span className="font-semibold text-slate-600">{label}</span>
                <span className="font-mono font-semibold">{val}</span>
              </div>
            ))}
            {/* <div className="flex justify-between px-5 py-2 border-b border-slate-200">
              <span className="font-bold text-slate-800">Total</span>
              <span className="font-mono font-bold text-slate-800">₹{formatNum(total)}</span>
            </div> */}
            <div className="flex justify-between px-5 py-3 bg-slate-900">
              <span className="font-black text-white text-[13px]">Total</span>
              <span className="font-mono font-black text-blue-400 text-[13px]">₹{formatNum(total)}</span>
            </div>
          </div>
        </div>

        {/* ── PAYMENT DETAILS ── */}
        <div className="flex gap-5 p-4 border-t border-slate-200">
          <div className="w-16 h-16 border-2 border-slate-200 rounded flex items-center justify-center text-[8px] text-slate-400 font-bold bg-slate-50">
            QR Code
          </div>
          <div className="text-[10px] text-slate-500 leading-relaxed">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Payment TO</p>
            <p className="font-bold text-slate-800">Shree Mobile Store</p>
            <p>HDFC BANK LTD · ACC NO.: 502 000 624 338 73</p>
            <p>IFSC CODE: HDFC0001452 · BRANCH: C.G ROAD CHEMBUR · ACC TYPE: CURRENT</p>
          </div>
        </div>

        {/* ── TERMS ── */}
        {/* <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-[10px] text-slate-500 leading-relaxed">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Terms & Conditions</p>
          <p>Thanks for your business. This is a computer generated document. No need of stamp.</p>
          <p>Once the product is delivered, we cannot take it back. No Return / No Refund.</p>
          <p>GSTIN: 27ABDC59362G1Z0</p>
        </div> */}

        {/* ── FOOTER BRAND ── */}
        <div className="flex justify-end px-5 py-3 border-t border-slate-200">
          <span className="font-black text-slate-900">Go<span className="text-blue-500">electronix</span></span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ROOT: CommissionsView
// ─────────────────────────────────────────────────────────────────────────────
export default function CommissionsView() {
  const [payoutList, setPayoutList] = useState(commissionPayouts);
  const [activeTab, setActiveTab]   = useState("billed"); // "billed" | "unbilled"
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [selectedSummary, setSelectedSummary] = useState(null); // New State

  const FILTERS = ["ALL", "MCP", "CP", "Promoter"];

  const Navigate = useNavigate();
  
  // ── Split lists ────────────────────────────────────────────────────────────
  const billedList   = useMemo(() => payoutList.filter(p => p.transferred),  [payoutList]);
  const unbilledList = useMemo(() => payoutList.filter(p => !p.transferred), [payoutList]);
  const activeList   = activeTab === "billed" ? billedList : unbilledList;

  // ── Monthly filter helper ──────────────────────────────────────────────────
  const isThisMonth = (p) => p.date?.startsWith(currentMonth);

  // ── Billed stats ───────────────────────────────────────────────────────────
  const totalBilled        = billedList.reduce((s, p) => s + p.payableAmount, 0);
  const monthlyBilled      = billedList.filter(isThisMonth).reduce((s, p) => s + p.payableAmount, 0);
  const totalBilledCount   = billedList.length;

  // ── Unbilled stats ─────────────────────────────────────────────────────────
  const totalUnbilled      = unbilledList.reduce((s, p) => s + p.payableAmount, 0);
  const monthlyUnbilled    = unbilledList.filter(isThisMonth).reduce((s, p) => s + p.payableAmount, 0);
  const totalUnbilledCount = unbilledList.length;

  // ── Toggle handler ─────────────────────────────────────────────────────────
  const handleToggle = (id) => {
    setPayoutList(prev => prev.map(p =>
      p.id === id
        ? {
            ...p,
            transferred: !p.transferred,
            date:  p.transferred ? p.date : todayISO(),
            txnId: p.transferred ? p.txnId : (p.txnId === "-" ? `MANUAL-${id}` : p.txnId),
          }
        : p
    ));
  };

  // ── Card configs ───────────────────────────────────────────────────────────
  const billedCards = [
    {
      label:    "Total Billed",
      value:    `₹${formatCurrency(totalBilled)}`,
      sublabel: `${totalBilledCount} payouts transferred`,
      icon:     CheckCircle2,
      accent:   "#059669",
      bg:       "#ECFDF5",
    },
    {
      label:    "Monthly Total Bill",
      value:    `₹${formatCurrency(monthlyBilled)}`,
      sublabel: `Transferred this month`,
      icon:     Calendar,
      accent:   "#2563EB",
      bg:       "#EFF6FF",
    },
    {
      label:    "Avg. Per Payout",
      value:    `₹${formatCurrency(totalBilledCount ? Math.round(totalBilled / totalBilledCount) : 0)}`,
      sublabel: "Average transferred amount",
      icon:     TrendingUp,
      accent:   "#7C3AED",
      bg:       "#F5F3FF",
    },
  ];

  const unbilledCards = [
    {
      label:    "Total Unbill",
      value:    `₹${formatCurrency(totalUnbilled)}`,
      sublabel: `${totalUnbilledCount} payouts pending`,
      icon:     Clock,
      accent:   "#D97706",
      bg:       "#FFFBEB",
    },
    {
      label:    "Monthly Total Unbill",
      value:    `₹${formatCurrency(monthlyUnbilled)}`,
      sublabel: "Pending this month",
      icon:     Calendar,
      accent:   "#EF4444",
      bg:       "#FEF2F2",
    },
    {
      label:    "Avg. Per Payout",
      value:    `₹${formatCurrency(totalUnbilledCount ? Math.round(totalUnbilled / totalUnbilledCount) : 0)}`,
      sublabel: "Average pending amount",
      icon:     AlertCircle,
      accent:   "#0891B2",
      bg:       "#ECFEFF",
    },
  ];

  

  const activeCards = activeTab === "billed" ? billedCards : "unbilled" ? unbilledCards : "current" ? unbilledCards : "";

  if (selectedSummary) {
    return <PayoutSummary data={selectedSummary} onBack={() => setSelectedSummary(null)} />;
  }

  return (
    <div className="space-y-5">

      {/* ── TAB BAR ── */}
      <div
        className="flex items-center"
        style={{ borderBottom:"2px solid #F1F5F9" }}
      >
        <TabButton
          label="Current Payout"
          active={activeTab === "current"}
          count={billedList.length}
          onClick={() => setActiveTab("current")}
          accent="#059669"
        />
        <TabButton
          label="Unbilled"
          active={activeTab === "unbilled"}
          count={unbilledList.length}
          onClick={() => setActiveTab("unbilled")}
          accent="#D97706"
        />
        <TabButton
          label="Billed"
          active={activeTab === "billed"}
          count={billedList.length}
          onClick={() => setActiveTab("billed")}
          accent="#059669"
        />
        
        
      </div>

      {/* ── KPI CARDS ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {activeCards.map((card, i) => (
          <StatCard key={card.label} {...card} index={i}/>
        ))}
      </div>

      {/* ── TABLE ── */}
      <SectionCard
        title={activeTab === "billed" ? "Billed Payouts Register" : "unbill" ? "Unbilled Payouts Register" : "current" ? "current month" : ""}
        right={
          <div className="flex justify-end gap-3 mb-5">
          <div className="flex gap-2 flex-wrap">
              {FILTERS.map((item) => (
                <button
                  key={item}
                  onClick={() => setTypeFilter(item)}
                  className="px-4 py-1.5 rounded-full text-xs font-bold transition-all"
                  style={{
                    background:
                      typeFilter === item ? "#2563EB" : "#F1F5F9",
                    color:
                      typeFilter === item ? "#FFFFFF" : "#64748B",
                    border:
                      typeFilter === item
                        ? "1px solid #2563EB"
                        : "1px solid #E2E8F0",
                  }}
                >
                  {item}
                </button>
              ))}
            </div>

            <SearchInput />

          <div>
            <span
              className="text-[11px] font-bold px-2.5 py-1 rounded-full"
              style={{
                background: activeTab === "billed" ? "#ECFDF5" : "#FFFBEB",
                color:      activeTab === "billed" ? "#059669" : "#D97706",
              }}
            >
              {activeList.length} records
            </span>
          </div>
          </div>
        }
      >
        <CommissionTable list={activeList} onToggle={handleToggle} activeTab={activeTab} Navigate={Navigate} setSelectedSummary={setSelectedSummary}/>
      </SectionCard>

      {/* ── NOTES ── */}
      {/* <SectionCard title="Admin Notes – Weekly Combined Statements">
        <ul className="list-disc list-inside text-xs text-slate-600 space-y-1.5">
          <li>Commissions from agent and retailer dashboards are combined into weekly statements.</li>
          <li>Each row records whether the amount is transferred, with bank name, account digits, txn ID and date.</li>
          <li>Use the action button to toggle between Billed and Unbilled status.</li>
          <li>Export to Excel/PDF for accounting and reconciliation with finance.</li>
        </ul>
      </SectionCard> */}

    </div>
  );
}


// import { Badge, CheckCircle2, IndianRupee } from "lucide-react";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { KpiCard } from "../../../Component/AdminComponents/KpiCard";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import { useState } from "react";


// const commissionPayouts = [
//   {
//     id: "PAY-9001",
//     forType: "Agent",
//     entityName: "Rahul Patil",
//     period: "Week 42 · 2025",
//     role: "Onboarding Commission",
//     commissionAmount: 22000,
//     payableAmount: 20000,
//     transferred: true,
//     bankName: "HDFC Bank",
//     accountLast4: "1234",
//     txnId: "TXN-HDFC-2025-001",
//     date: "2025-10-25",
//   },
//   {
//     id: "PAY-9002",
//     forType: "Retailer",
//     entityName: "Shree Mobile Store",
//     period: "Week 42 · 2025",
//     role: "Warranty Sales Payout",
//     commissionAmount: 12000,
//     payableAmount: 12000,
//     transferred: false,
//     bankName: "ICICI Bank",
//     accountLast4: "5678",
//     txnId: "-",
//     date: "2025-10-27",
//   },
//   {
//     id: "PAY-9003",
//     forType: "Agent",
//     entityName: "Priya Singh",
//     period: "Week 41 · 2025",
//     role: "Onboarding Commission",
//     commissionAmount: 15000,
//     payableAmount: 15000,
//     transferred: true,
//     bankName: "SBI",
//     accountLast4: "9999",
//     txnId: "TXN-SBI-2025-221",
//     date: "2025-10-20",
//   },
// ];


// const todayISO = () => new Date().toISOString().slice(0, 10);

// export default function CommissionsView() {

//   const [payoutList, setPayoutList] = useState(commissionPayouts);
//   const totalCommission = payoutList.reduce((sum, row) => sum + (row.commissionAmount || 0), 0);
//   const totalPayable = payoutList.reduce((sum, row) => sum + (row.payableAmount || 0), 0);
//   const totalTransferred = payoutList
//     .filter((row) => row.transferred)
//     .reduce((sum, row) => sum + (row.payableAmount || 0), 0);

//   const handleMarkPayoutTransferred = (id) => {
//     setPayoutList((prev) =>
//       prev.map((p) =>
//         p.id === id
//           ? {
//               ...p,
//               transferred: !p.transferred,
//               date: todayISO(),
//               txnId: p.transferred ? p.txnId : p.txnId === "-" ? `MANUAL-${id}` : p.txnId,
//             }
//           : p
//       )
//     );
//   };

//   return (
//     <div className="space-y-4">
//       <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
//         <KpiCard
//           label="Total Unbilled"
//           value={`₹ ${formatCurrency(totalCommission)}`}
//           subLabel="Agent + Retailer"
//           icon={IndianRupee}
//         />
//         <KpiCard
//           label="Monthly Total unbill"
//           value={`₹ ${formatCurrency(totalPayable)}`}
//           subLabel="Approved to be paid"
//           icon={IndianRupee}
//         />
//         <KpiCard
//           label="Transferred"
//           value={`₹ ${formatCurrency(totalTransferred)}`}
//           subLabel=""
//           icon={CheckCircle2}
//         />
//       </div>

//       <SectionCard title="Commission Payout Register">
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">Payout</th>
//                 <th className="py-2 text-left">Entity</th>
//                 <th className="py-2 text-left">Role / Period</th>
//                 <th className="py-2 text-left">Amounts (₹)</th>
//                 <th className="py-2 text-left">Bank / Txn</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Admin Action</th>
//               </tr>
//             </thead>
//             <tbody>
//               {payoutList.map((p) => (
//                 <tr
//                   key={p.id}
//                   className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                 >
//                   <td className="py-1.5 text-xs text-slate-700 font-medium">{p.id}</td>
//                   <td className="py-1.5 text-xs text-slate-700">
//                     <div className="font-semibold">{p.entityName}</div>
//                     <div className="text-[10px] text-slate-500">{p.forType}</div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>{p.role}</div>
//                     <div className="text-[10px] text-slate-500">{p.period}</div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>
//                       Commission: ₹ {formatCurrency(p.commissionAmount || 0)}
//                     </div>
//                     <div className="text-[10px] text-slate-500">
//                       Payable: ₹ {formatCurrency(p.payableAmount || 0)}
//                     </div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>
//                       {p.bankName} · ****{p.accountLast4}
//                     </div>
//                     <div className="text-[10px] text-slate-500">Txn: {p.txnId}</div>
//                     <div className="text-[10px] text-slate-500">Date: {p.date}</div>
//                   </td>
//                   <td className="py-1.5 text-xs">
//                     <Badge color={p.transferred ? "green" : "yellow"}>
//                       {p.transferred ? "Transferred" : "Pending"}
//                     </Badge>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <button
//                       onClick={() => handleMarkPayoutTransferred(p.id)}
//                       className="text-[10px] px-2 py-0.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
//                     >
//                       {p.transferred ? "Mark as pending" : "Mark as transferred"}
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </SectionCard>

//       <SectionCard title="Admin Notes – Weekly Combined Statements">
//         <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//           <li>
//             Commissions from agent dashboard and retailer dashboard are combined
//             into weekly statements.
//           </li>
//           <li>
//             Each row records whether the amount is actually transferred, with bank
//             name, account last digits, transaction id and date.
//           </li>
//           <li>
//             Export to Excel/PDF for accounting and reconciliation with finance.
//           </li>
//         </ul>
//       </SectionCard>
//     </div>
//   );
// }